import * as React from 'react';
import styled from 'styled-components';
import { Formik, Form, Field } from 'formik';

import { describeExpression } from 'util/CronUtils';
import { Button, ControlLabel, Input } from 'components/bootstrap';
import { Switch, Spinner } from 'components/common';
import { MarkdownPreview } from 'components/common/MarkdownEditor';
import { Row, Col } from 'security-app/components/common/FormBaseStyles';
import { DataWell } from 'common/components/layout-styles';
import {
  SubmitOnValueChange,
  FormikMultiSelect,
  FormikTimeUnitInput,
  FormikUserTimezoneSelect,
  FormikSearchFiltersFormControl,
  useStreamOptions,
  useNotificationOptions,
} from 'security-app/components/common/FormikComponents';
import {
  EventProcedureFormField,
  ShareFormFormikField,
} from 'security-app/components/SecurityEvents/EventProcedures/plugin/EventProcedureForm';
import EventProcedureSummary from 'security-app/components/SecurityEvents/EventProcedures/plugin/EventProcedureSummary';
import type { FormStateType } from 'security-app/components/common/FormikComponents';
import type { RuleOptionsType } from 'security-app/hooks/api/sigmaAPI.types';

import RuleOptionsReadOnly from './ReadOnly';
import { SEARCH_WITHIN_UNITS, EXECUTE_EVERY_UNITS, validate } from './utils';

const StyledForm = styled(Form)`
  width: 100%;

  & .form-group {
    width: 100%;
  }
`;

const StyledHelp = styled.small`
  white-space: pre-wrap;
`;

type Props = {
  initialValues: RuleOptionsType;
  formId?: string;
  onSubmit: (values: RuleOptionsType) => void;
  onCancel?: () => void;
  validateOnMount?: boolean;
  isEditing?: boolean;
  autoSubmit?: boolean;
  formState?: (value: FormStateType) => void;
  notMutable?: boolean;
  readOnly?: boolean;
  isTemporal?: boolean;
};

function RuleOptionsForm({
  initialValues,
  formId = 'ruleOptionsForm',
  onSubmit,
  onCancel = () => {},
  validateOnMount = false,
  isEditing = false,
  autoSubmit = false,
  formState = () => {},
  notMutable = false,
  readOnly = false,
  isTemporal = false,
}: Props) {
  const { streamOptions, streamCategoryOptions, loadingStreams } = useStreamOptions();
  const { notificationOptions, loadingNotifications } = useNotificationOptions();
  const [showAddEventProcedureForm, setShowAddEventProcedureForm] = React.useState<boolean>(false);

  const showEventProcedureSummar = React.useMemo(
    () => !!initialValues?.event_procedure_id && !showAddEventProcedureForm,
    [initialValues?.event_procedure_id, showAddEventProcedureForm],
  );
  const showAddNewEventProcedure = React.useMemo(
    () => !initialValues?.event_procedure_id && !showAddEventProcedureForm && !readOnly && !notMutable,
    [initialValues?.event_procedure_id, showAddEventProcedureForm, readOnly, notMutable],
  );
  const showRemediationSteps = React.useMemo(
    () => !!initialValues.remediation_steps && !initialValues.event_procedure_id && !showAddEventProcedureForm,
    [initialValues.remediation_steps, initialValues.event_procedure_id, showAddEventProcedureForm],
  );

  const handleCancel = (resetForm: () => void) => () => {
    resetForm();
    onCancel();
  };

  const preventSubmitOnEnter = (e: React.KeyboardEvent) => e.key !== 'Enter';

  if (readOnly) return <RuleOptionsReadOnly ruleOptions={initialValues} />;

  return (
    <Formik onSubmit={onSubmit} initialValues={initialValues} validate={validate} validateOnMount={validateOnMount}>
      {({ values, errors, isValid, dirty, submitForm, resetForm, setFieldValue }) => (
        <StyledForm id={formId} onKeyDown={preventSubmitOnEnter}>
          <Col $width="100%">
            {loadingStreams ? (
              <Spinner text="Loading stream options" />
            ) : (
              !isTemporal && (
                <Field
                  name="streams"
                  component={FormikMultiSelect}
                  options={streamOptions}
                  value={values.streams}
                  label="Streams"
                  multiple
                  menuPlacement="bottom"
                  disabled={notMutable || isTemporal}
                />
              )
            )}
            {streamCategoryOptions.length > 0 && !isTemporal && (
              <Field
                name="stream_categories"
                component={FormikMultiSelect}
                options={streamCategoryOptions}
                value={values.stream_categories}
                label="Stream categories"
                multiple
                menuPlacement="bottom"
                disabled={notMutable || isTemporal}
              />
            )}
            {loadingNotifications ? (
              <Spinner text="Loading notifications" />
            ) : (
              <Field
                name="notifications"
                component={FormikMultiSelect}
                options={notificationOptions}
                value={values.notifications}
                label="Notifications"
                multiple
                menuPlacement="bottom"
              />
            )}
            <Field
              name="search_within"
              component={FormikTimeUnitInput}
              label="Search within"
              value={values.search_within}
              units={SEARCH_WITHIN_UNITS}
              errors={errors}
              disabled={notMutable}
            />
            <Row $width="100%" $fullWidth>
              <Field name="use_cron_scheduling" type="checkbox">
                {({ field }) => (
                  <Switch
                    name={field.name}
                    id={`${formId}_${field.name}`}
                    label="Use cron scheduling"
                    checked={field.checked}
                    onChange={field.onChange}
                    disabled={notMutable}
                  />
                )}
              </Field>
            </Row>
            {values.use_cron_scheduling ? (
              <Col $width="100%" $fullWidth>
                <Field name="cron_expression" style={{ width: '100%' }}>
                  {({ field }) => (
                    <Input
                      id={field.name}
                      name={field.name}
                      label="Cron expression"
                      type="text"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      help={
                        <StyledHelp>
                          {describeExpression(field.value) ||
                            'A Quartz cron expression to determine when the event should be run.'}
                        </StyledHelp>
                      }
                      bsStyle={field.error && field.touched ? 'error' : null}
                    />
                  )}
                </Field>
                <Field component={FormikUserTimezoneSelect} id="cron_timezone" name="cron_timezone" />
              </Col>
            ) : (
              <Field
                name="execute_every"
                component={FormikTimeUnitInput}
                label="Execute every"
                value={values.execute_every}
                units={EXECUTE_EVERY_UNITS}
                errors={errors}
                disabled={notMutable}
              />
            )}
            {!isTemporal && (
              <Row $fullWidth $width="100%">
                <Field
                  component={FormikSearchFiltersFormControl}
                  name="filters"
                  value={values.filters}
                  label="Search Filters"
                />
              </Row>
            )}
            {showAddEventProcedureForm && (
              <Row $fullWidth $width="100%">
                <Field
                  name="event_procedure_id"
                  id="event_procedure_id"
                  value={initialValues?.event_procedure_id}
                  component={EventProcedureFormField}
                  remediationSteps={values.remediation_steps}
                  onClose={() => setShowAddEventProcedureForm(false)}
                  onRemove={() => setShowAddEventProcedureForm(false)}
                />
              </Row>
            )}
            {showRemediationSteps && (
              <Col $width="100%">
                <ControlLabel>Remediation Steps</ControlLabel>
                <DataWell>
                  <MarkdownPreview
                    value={initialValues.remediation_steps}
                    maxHeight={500}
                    show
                    noBackground
                    noBorder
                    noPadding
                  />
                </DataWell>
              </Col>
            )}
            {showEventProcedureSummar && (
              <Col $width="100%">
                <ControlLabel>Event Procedure Summary</ControlLabel>
                <EventProcedureSummary
                  eventProcedureId={initialValues.event_procedure_id}
                  canEdit={!notMutable}
                  onEdit={() => setShowAddEventProcedureForm(true)}
                  onRemove={() => {
                    setFieldValue('event_procedure_id', '');
                    setShowAddEventProcedureForm(false);
                  }}
                />
              </Col>
            )}
            {showAddNewEventProcedure && (
              <Col $gap="0.4rem">
                <ControlLabel>Event Procedures</ControlLabel>
                <p>This rule does not have any Event Procedures yet.</p>
                <Button bsStyle="primary" onClick={() => setShowAddEventProcedureForm(true)}>
                  Add Event Procedure
                </Button>
              </Col>
            )}
            {!isEditing && (
              <Field
                name="share_request"
                id="share_request"
                value={values.share_request}
                component={ShareFormFormikField}
                description="Search for a User or Team to add as collaborator on the uploaded sigma rules."
                entityType="sigma_rule"
              />
            )}
            {autoSubmit ? (
              <SubmitOnValueChange formState={formState} />
            ) : (
              <Row>
                <Button type="reset" bsSize="xs" onClick={handleCancel(resetForm)}>
                  Cancel
                </Button>
                <Button type="button" bsStyle="primary" bsSize="xs" onClick={submitForm} disabled={!isValid || !dirty}>
                  Save Options
                </Button>
              </Row>
            )}
          </Col>
        </StyledForm>
      )}
    </Formik>
  );
}

export default RuleOptionsForm;
